import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import { useHall, useRemoveHallSlot, useSaveHallSlot } from '@/api/hooks'
import type { Hall, HallSlot } from '@/api/types'
import { ErrorBanner, formatMoney, formatSlotHours, TimeField } from '@/components/common'

/**
 * The slots a space is sold in, set up by hand.
 *
 * <p>There are no default slots: a space is sold in whatever sittings it really
 * runs, and inventing them would offer windows the hall does not sell. A slot
 * withdrawn here stops being offered but is not deleted, so a booking already
 * taken against it still reads.</p>
 */
export function SlotEditor({ space }: { space: Hall }) {
  // Read the space back rather than trusting the object the dialog was opened
  // with: adding or withdrawing a slot changes it, and a stale copy would show
  // the list as it was before the change.
  const { data: live } = useHall(space.id)
  const save = useSaveHallSlot()
  const remove = useRemoveHallSlot()
  const [open, setOpen] = useState(false)
  // The code of the slot being corrected, or null when the form is adding a new
  // one. The code identifies the slot server-side, so editing keeps it fixed.
  const [editingCode, setEditingCode] = useState<string | null>(null)
  const [slotCode, setSlotCode] = useState('')
  const [name, setName] = useState('')
  const [startTime, setStartTime] = useState('07:00')
  const [endTime, setEndTime] = useState('15:00')
  const [rentalCharge, setRentalCharge] = useState('')
  const [cleaningCharge, setCleaningCharge] = useState('')
  const [cleaningNotes, setCleaningNotes] = useState('')

  const slots = (live ?? space).timeSlots ?? []
  const valid = Boolean(slotCode.trim() && name.trim() && startTime && endTime && endTime > startTime)

  const clear = () => {
    setOpen(false)
    setEditingCode(null)
    setSlotCode('')
    setName('')
    setStartTime('07:00')
    setEndTime('15:00')
    setRentalCharge('')
    setCleaningCharge('')
    setCleaningNotes('')
  }

  /** Loads a slot into the form so its hours and charges can be corrected. */
  const edit = (slot: HallSlot) => {
    setEditingCode(slot.slotCode)
    setSlotCode(slot.slotCode)
    setName(slot.name)
    setStartTime(slot.startTime.slice(0, 5))
    setEndTime(slot.endTime.slice(0, 5))
    setRentalCharge(slot.rentalCharge ? String(slot.rentalCharge) : '')
    setCleaningCharge(slot.cleaningCharge ? String(slot.cleaningCharge) : '')
    setCleaningNotes(slot.cleaningNotes ?? '')
    setOpen(true)
  }

  const add = () =>
    save.mutate(
      {
        hallId: space.id,
        body: {
          slotCode: slotCode.trim().toUpperCase().replace(/\s+/g, '_'),
          name: name.trim(),
          startTime: `${startTime}:00`,
          endTime: `${endTime}:00`,
          rentalCharge: rentalCharge ? Number(rentalCharge) : 0,
          cleaningCharge: cleaningCharge ? Number(cleaningCharge) : 0,
          cleaningNotes: cleaningNotes.trim() || undefined,
        },
      },
      { onSuccess: clear },
    )

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Slots</Typography>
        <Button
          size="small"
          startIcon={open ? undefined : <AddIcon />}
          onClick={() => (open ? clear() : setOpen(true))}
        >
          {open ? 'Cancel' : 'Add slot'}
        </Button>
      </Stack>
      <ErrorBanner error={save.error ?? remove.error} />

      {slots.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No slots yet. This space cannot be booked until it has at least one.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Slot</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell align="right">Rental</TableCell>
              <TableCell align="right">Cleaning</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {slots.map((slot) => (
              <TableRow key={slot.id} hover>
                <TableCell>
                  <Typography variant="body2">{slot.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {slot.slotCode}
                  </Typography>
                </TableCell>
                <TableCell>{formatSlotHours(slot.startTime, slot.endTime)}</TableCell>
                <TableCell align="right">{formatMoney(slot.rentalCharge)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{formatMoney(slot.cleaningCharge)}</Typography>
                  {slot.cleaningNotes ? (
                    <Typography variant="caption" color="text.secondary">
                      {slot.cleaningNotes}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton size="small" aria-label={`Edit ${slot.name}`} onClick={() => edit(slot)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={remove.isPending}
                    aria-label={`Remove ${slot.name}`}
                    onClick={() => remove.mutate({ hallId: space.id, slotId: slot.id })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {open ? (
        <>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} alignItems="flex-start">
          <TextField
            size="small"
            label="Code"
            value={slotCode}
            onChange={(event) => setSlotCode(event.target.value.toUpperCase())}
            placeholder="SLOT_1"
            // The code is how a booking points at its slot, so it is fixed once
            // the slot exists; everything else about it can be corrected.
            disabled={editingCode !== null}
            helperText={editingCode ? 'Codes cannot change' : undefined}
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Slot 1"
            sx={{ flexGrow: 1, minWidth: 140 }}
          />
          <TimeField
            label="From"
            value={startTime}
            onChange={setStartTime}
            sx={{ width: 150 }}
          />
          <TimeField
            label="To"
            value={endTime}
            onChange={setEndTime}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="number"
            label="Rental"
            value={rentalCharge}
            onChange={(event) => setRentalCharge(event.target.value)}
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            type="number"
            label="Cleaning"
            value={cleaningCharge}
            onChange={(event) => setCleaningCharge(event.target.value)}
            sx={{ width: 120 }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} alignItems="flex-start">
          <TextField
            size="small"
            label="Cleaning charge covers"
            value={cleaningNotes}
            onChange={(event) => setCleaningNotes(event.target.value.slice(0, 300))}
            placeholder="What the charge covers, and when it goes up"
            inputProps={{ maxLength: 300 }}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="contained" size="small" disabled={!valid || save.isPending} onClick={add}>
            {editingCode ? 'Save slot' : 'Add'}
          </Button>
        </Stack>
        </>
      ) : null}
    </Box>
  )
}
